import { useEffect, useState } from 'react';
import type { LineaDesglosada, LineaMostrador } from '@tanteo/compartido';
import { CANTIDAD_MAXIMA, formatearCOP, netoDeLinea, totalLinea } from '@tanteo/compartido';
import { CeldaPlata, EncabezadoTabla, EstadoVacio, FilaTabla, NumeroEnLinea, TablaAncha, ThAcciones } from '../../ui/base';

export function PanelLineas({
  lineas,
  desglose,
  mostrarDescuentos,
  mostrarImpuestos,
  onCantidad,
  onQuitar,
  onLectura,
  onDescuento,
  onQuitarDescuento,
  descuentoBloqueado,
}: {
  lineas: LineaMostrador[];
  /** El desglose de cada línea, en el mismo orden, sin el descuento a la venta. */
  desglose: LineaDesglosada[];
  /** Sin descuentos ni impuestos en el negocio, sus columnas no aparecen. */
  mostrarDescuentos: boolean;
  mostrarImpuestos: boolean;
  onCantidad: (sku: string, cantidad: number) => void;
  onQuitar: (sku: string) => void;
  /** Un código que el lector tecleó en una cantidad: la cantidad no cambia y el código se registra. */
  onLectura: (codigo: string, terminado: boolean) => void;
  /** Sin él, el negocio no permite descuentos a este rol y la línea no muestra el botón. */
  onDescuento?: (sku: string) => void;
  onQuitarDescuento?: (sku: string) => void;
  /** Con un descuento a la venta puesto, los productos no admiten el suyo: el % se ve apagado y al tocarlo dice esto. */
  descuentoBloqueado?: string | null;
}) {
  const maxDigitos = String(CANTIDAD_MAXIMA).length;
  // El porqué del % apagado se dice al tocarlo, y se apaga solo o cuando ya no hay descuento a la venta.
  const [avisoBloqueo, setAvisoBloqueo] = useState(false);
  useEffect(() => {
    if (!avisoBloqueo) return;
    if (!descuentoBloqueado) {
      setAvisoBloqueo(false);
      return;
    }
    const reloj = setTimeout(() => setAvisoBloqueo(false), 5000);
    return () => clearTimeout(reloj);
  }, [avisoBloqueo, descuentoBloqueado]);
  if (lineas.length === 0) {
    return <EstadoVacio titulo="Escanea el primer producto" detalle="Cada lectura suma una unidad; la cantidad se puede editar." />;
  }
  // Con tope y scroll propio: sin esto la tarjeta crece una fila por línea y empuja el total y
  // «CERRAR VENTA» fuera de la pantalla. Caben unas siete líneas a la vista.
  return (
    <div>
      <TablaAncha data-e2e="lineas" claseCaja="max-h-[17rem] overflow-y-auto" e2eCaja="lineas-scroll">
        <EncabezadoTabla fijo>
            <th className="py-2 pr-2 w-24">Cant.</th>
            <th className="py-2 pr-2">Producto</th>
            <th className="py-2 pr-2 text-right">P. catálogo</th>
            {mostrarDescuentos && <th className="py-2 pr-2 text-right">Descuento</th>}
            <th className="py-2 pr-2 text-right">P. venta</th>
            {mostrarImpuestos && <th className="py-2 pr-2 text-right">Impuesto</th>}
            <th className="py-2 pr-2 text-right">Subtotal</th>
            <ThAcciones className={onDescuento ? 'w-20' : 'w-10'} />
        </EncabezadoTabla>
        <tbody>
          {lineas.map((l, i) => {
            // El neto sale del desglose: un lleva y paga depende de las demás líneas de la promoción.
            const neto = desglose[i]?.neto ?? netoDeLinea(l);
            const conDescuento = neto !== totalLinea(l);
            const impuesto = desglose[i]?.impuesto ?? 0;
            // Por unidad, como en el diálogo del producto: el subtotal, con su impuesto, es la cifra exacta.
            const precioVenta = Math.round(neto / l.cantidad);
            return (
              <FilaTabla key={l.sku}>
                <td className="py-1.5 pr-2">
                  <NumeroEnLinea
                    ancho="corto"
                    alineacion="centro"
                    min={1}
                    max={CANTIDAD_MAXIMA}
                    valor={l.cantidad}
                    onValor={(n) => onCantidad(l.sku, n ?? 1)}
                    lector={{ maxDigitos, alLeer: onLectura }}
                    aria-label={`Cantidad de ${l.nombre}`}
                  />
                </td>
                <td className="py-1.5 pr-2 font-semibold">{l.nombre}</td>
                <CeldaPlata className="py-1.5">{formatearCOP(l.precioUnit)}</CeldaPlata>
                {mostrarDescuentos && (
                <td className="num py-1.5 pr-2 text-right">
                  {conDescuento && l.descuento ? (
                    <>
                      <span className="text-destructive font-bold">−{formatearCOP(l.precioUnit - precioVenta)}</span>
                      <span className="text-muted flex items-center justify-end gap-1 text-etiqueta" data-e2e="linea-descuento">
                        {l.descuento.nombre}
                        {onQuitarDescuento && (
                          <button
                            tabIndex={-1}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => onQuitarDescuento(l.sku)}
                            aria-label={`Quitar el descuento de ${l.nombre}`}
                          >
                            ✕
                          </button>
                        )}
                      </span>
                    </>
                  ) : (
                    <span className="text-muted">{formatearCOP(0)}</span>
                  )}
                </td>
                )}
                <CeldaPlata className="py-1.5">{formatearCOP(precioVenta)}</CeldaPlata>
                {mostrarImpuestos && (
                  <td className="num py-1.5 pr-2 text-right" data-e2e="linea-impuesto">
                    {impuesto > 0 ? (
                      <>
                        <span>+{formatearCOP(Math.round(impuesto / l.cantidad))}</span>
                        <span className="text-muted block text-etiqueta">{l.impuestos.map((imp) => imp.nombre).join(' + ')}</span>
                      </>
                    ) : (
                      <span className="text-muted">{formatearCOP(0)}</span>
                    )}
                  </td>
                )}
                <CeldaPlata fuerte className="py-1.5">
                  {formatearCOP(neto + impuesto)}
                </CeldaPlata>
                <td className="py-1.5 text-right whitespace-nowrap">
                  {onDescuento && (
                    <button
                      tabIndex={-1}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => (descuentoBloqueado ? setAvisoBloqueo(true) : onDescuento(l.sku))}
                      aria-disabled={!!descuentoBloqueado}
                      className={`rounded-control border-border mr-1 border px-2 py-1 font-bold ${
                        descuentoBloqueado ? 'border-dashed opacity-40' : ''
                      }`}
                      aria-label={`Descuento a ${l.nombre}`}
                      data-e2e="linea-descuento-abrir"
                    >
                      %
                    </button>
                  )}
                  <button
                    onClick={() => onQuitar(l.sku)}
                    className="text-destructive rounded-control px-2 py-1 font-bold hover:bg-destructive/10"
                    aria-label={`Quitar ${l.nombre}`}
                  >
                    ✕
                  </button>
                </td>
              </FilaTabla>
            );
          })}
        </tbody>
      </TablaAncha>
      {descuentoBloqueado && avisoBloqueo && (
        <p className="text-destructive mt-2 text-contenido font-semibold" role="status" data-e2e="descuento-producto-bloqueado">
          {descuentoBloqueado}
        </p>
      )}
    </div>
  );
}
